import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Shield, Zap, LineChart, ArrowRight, Star, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '../utils/api';

// TradeForge Logo Component
const TradeForgelogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://static.prod-images.emergentagent.com/jobs/df6b9931-f35a-4f03-9adf-4a4572c904c3/images/6dfa2d55642f0060455034e074f9f811d7710ca6e7452f73c1fd5e309a79f2ae.png"
    alt="TradeForge"
    className={className}
  />
);

export default function Landing() {
  const navigate = useNavigate();
  const [publicStats, setPublicStats] = useState({
    total_signals: null,
    win_rate: null,
    loading: true
  });

  useEffect(() => {
    const fetchPublicStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/public`, { timeout: 5000 });
        if (response.data) {
          setPublicStats({
            total_signals: response.data.total_signals,
            win_rate: response.data.win_rate,
            loading: false
          });
        }
      } catch (error) {
        setPublicStats(prev => ({ ...prev, loading: false }));
      }
    };
    fetchPublicStats();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/dashboard');
        }
      } catch (error) {
        // Not authenticated
      }
    };
    checkAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#060A10] text-white overflow-hidden">
      {/* Hero Section */}
      <div className="relative">
        {/* Animated background effects - Cyan/Blue theme */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[120px] animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-[40%] left-[50%] w-[300px] h-[300px] bg-cyan-500/3 rounded-full blur-[100px]"></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,216,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,216,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <nav className="flex items-center justify-between py-6 border-b border-[#1E293B]">
            <div className="flex items-center gap-3">
              <TradeForgelogo className="w-11 h-11" />
              <span className="text-2xl font-bold font-['Manrope'] tracking-tight bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                TradeForge
              </span>
            </div>
            <div className="flex gap-3 items-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/pricing')}
                className="text-slate-400 hover:text-cyan-400 hover:bg-[#0D131C]"
              >
                Pricing
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="text-slate-400 hover:text-cyan-400 hover:bg-[#0D131C]"
              >
                Log In
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                data-testid="nav-signup-btn"
                className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold px-6"
              >
                Get Started
              </Button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="text-center py-24 lg:py-32">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#0D131C] border border-[#1E293B] rounded-md px-4 py-2 mb-8">
              <Star className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300">Professional AI-Powered Trading Signals</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold font-['Manrope'] tracking-tight mb-6 leading-[1.1]">
              Trade Smarter.
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                Execute Faster.
              </span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
              Real-time trading signals with institutional-grade analysis. 
              Clear entries, precise stops, and calculated risk-reward ratios.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/signup')}
                data-testid="hero-get-started-btn"
                className="bg-cyan-500 text-black hover:bg-cyan-400 text-lg px-8 py-6 font-bold rounded-md"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="hero-view-signals-btn"
                className="border-[#1E293B] hover:bg-[#0D131C] hover:border-cyan-500/50 text-lg px-8 py-6 rounded-md"
              >
                View Live Signals
              </Button>
            </div>
            
            {/* Stats */}
            <div className="flex items-center justify-center gap-12 mt-16 pt-8 border-t border-[#1E293B]">
              <div>
                <p className="text-3xl font-bold font-mono text-white">
                  {publicStats.loading ? '...' : publicStats.total_signals !== null ? `${publicStats.total_signals}+` : 'Live'}
                </p>
                <p className="text-sm text-slate-500">Signals Delivered</p>
              </div>
              <div>
                <p className="text-3xl font-bold font-mono text-[#00E676]">
                  {publicStats.loading ? '...' : publicStats.win_rate !== null ? `${publicStats.win_rate}%` : 'Tracking'}
                </p>
                <p className="text-sm text-slate-500">Win Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold font-mono text-cyan-400">24/7</p>
                <p className="text-sm text-slate-500">Market Coverage</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold font-['Manrope'] tracking-tight mb-4">Why TradeForge?</h2>
          <p className="text-slate-400 text-lg">Everything you need for confident trading decisions</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="group bg-[#0D131C] border border-[#1E293B] rounded-md p-8 hover:border-cyan-500/50 transition-all duration-150">
            <div className="w-14 h-14 bg-cyan-500/10 rounded-md flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <LineChart className="w-7 h-7 text-cyan-400" />
            </div>
            <h3 className="text-xl font-semibold font-['Manrope'] mb-3 text-white">Real-Time Analysis</h3>
            <p className="text-slate-400 leading-relaxed">
              Multi-timeframe analysis with trend identification, key levels, and momentum confirmation.
            </p>
          </div>

          <div className="group bg-[#0D131C] border border-[#1E293B] rounded-md p-8 hover:border-blue-500/50 transition-all duration-150">
            <div className="w-14 h-14 bg-blue-500/10 rounded-md flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Shield className="w-7 h-7 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold font-['Manrope'] mb-3 text-white">Risk Management</h3>
            <p className="text-slate-400 leading-relaxed">
              Every signal includes precise entry zones, stop loss, take profit, and risk-reward ratios.
            </p>
          </div>

          <div className="group bg-[#0D131C] border border-[#1E293B] rounded-md p-8 hover:border-purple-500/50 transition-all duration-150">
            <div className="w-14 h-14 bg-purple-500/10 rounded-md flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Zap className="w-7 h-7 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold font-['Manrope'] mb-3 text-white">Instant Alerts</h3>
            <p className="text-slate-400 leading-relaxed">
              Get notified immediately via Telegram, email, and in-app when new opportunities emerge.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden bg-[#0D131C] border border-[#1E293B] rounded-md p-12 text-center">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,rgba(0,216,255,0.1),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,0.1),transparent_50%)]"></div>
          
          <div className="relative">
            <h2 className="text-4xl font-bold font-['Manrope'] tracking-tight mb-4">Ready to Trade Like a Pro?</h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Join professional traders using AI-powered signals for disciplined, data-driven entries.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/signup')}
              data-testid="cta-get-started-btn"
              className="bg-cyan-500 text-black hover:bg-cyan-400 text-lg px-8 py-6 font-bold rounded-md"
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1E293B] py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TradeForgelogo className="w-9 h-9" />
                <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  TradeForge
                </span>
              </div>
              <p className="text-slate-500 text-sm">
                Professional trading signals and market analysis. Trade smarter with AI-powered insights.
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-slate-300">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><a href="/login" className="hover:text-cyan-400 transition-colors">Login</a></li>
                <li><a href="/signup" className="hover:text-cyan-400 transition-colors">Sign Up</a></li>
                <li><a href="/pricing" className="hover:text-cyan-400 transition-colors">Pricing</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-slate-300">Contact Us</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <a href="mailto:supporteam@gettradeforgeapp.com" className="hover:text-cyan-400 transition-colors">
                    supporteam@gettradeforgeapp.com
                  </a>
                </li>
                <li className="text-xs text-slate-600 mt-2">
                  Support available 24/7
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#1E293B] pt-8 text-center text-slate-500 text-sm">
            <p>© 2026 TradeForge. All rights reserved. Educational purposes only. Not financial advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
